import * as React from 'react';
import * as SitePopover from '@/components/ui/popover';
import * as OsPopover from '@/OS/components/ui/popover';

type EmbeddedProp = {
  embedded?: boolean;
};

const EmbeddedPopoverContext = React.createContext<boolean | undefined>(undefined);

const detectEmbedded = () => typeof document !== 'undefined' && Boolean(document.querySelector('.mapleos-root'));

const useEmbeddedPopover = (embedded?: boolean) => embedded ?? React.useContext(EmbeddedPopoverContext) ?? detectEmbedded();

export function Popover({ embedded, children, ...props }: React.ComponentProps<typeof SitePopover.Popover> & EmbeddedProp) {
  const currentEmbedded = useEmbeddedPopover(embedded);
  const Component = (currentEmbedded ? OsPopover.Popover : SitePopover.Popover) as typeof SitePopover.Popover;

  return (
    <EmbeddedPopoverContext.Provider value={currentEmbedded}>
      <Component {...props}>{children}</Component>
    </EmbeddedPopoverContext.Provider>
  );
}

export const PopoverTrigger = React.forwardRef<
  React.ElementRef<typeof SitePopover.PopoverTrigger>,
  React.ComponentPropsWithoutRef<typeof SitePopover.PopoverTrigger> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedPopover(embedded);
  const Component = (currentEmbedded ? OsPopover.PopoverTrigger : SitePopover.PopoverTrigger) as typeof SitePopover.PopoverTrigger;

  return <Component ref={ref as never} {...props} />;
});
PopoverTrigger.displayName = 'OSAwarePopoverTrigger';

export function PopoverAnchor(props: React.ComponentProps<typeof SitePopover.PopoverAnchor>) {
  return <SitePopover.PopoverAnchor {...props} />;
}

export const PopoverContent = React.forwardRef<
  React.ElementRef<typeof SitePopover.PopoverContent>,
  React.ComponentPropsWithoutRef<typeof SitePopover.PopoverContent> & EmbeddedProp
>(({ embedded, ...props }, ref) => {
  const currentEmbedded = useEmbeddedPopover(embedded);
  const Component = (currentEmbedded ? OsPopover.PopoverContent : SitePopover.PopoverContent) as typeof SitePopover.PopoverContent;

  return <Component ref={ref as never} {...props} />;
});
PopoverContent.displayName = 'OSAwarePopoverContent';
